import { cache } from 'react';

import listingAdapter from '@/features/listing/requests/adapters/listing-adapter';
import {
  type ListingsCollection,
  type ListingWithUser,
} from '@/features/listing/types/listing-types';
import {
  type ListingsSearchGeo,
  type ListingsSearchResponse,
} from '@/features/listing/types/listings-search-response-types';
import userAdapter from '@/features/user/requests/adapters/user-adapter';
import getUsersIdVerifications from '@/features/user/requests/get-users-id-verifications';
import { type UsersIdVerificationsCollection } from '@/features/user/types/user-id-verification-types';
import { logError, logWarn } from '@/libs/logger/console-log';
import sendHttpError from '@/libs/logger/requests/send-http-error';

function getSearchGeoBbox(geo: ListingsSearchGeo): ListingsCollection['searchGeoBbox'] {
  return {
    latitudeNorthEast: geo.lat_ne,
    latitudeSouthWest: geo.lat_sw,
    longitudeNorthEast: geo.lng_ne,
    longitudeSouthWest: geo.lng_sw,
  };
}

export default cache(async function getListings(
  searchParams: string,
  currentCurrencyName: string,
  bearerToken: string | null,
  next?: NextFetchRequestConfig,
  includeUserVerifications = true,
): Promise<ListingsCollection> {
  const url = `${process.env.NEXT_PUBLIC_API_URL}/v1/search?${searchParams}`;
  const headers: HeadersInit = { Currency: currentCurrencyName };

  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken}`;
  }

  let listingsResponse: Response;

  try {
    listingsResponse = await fetch(
      url,
      {
        ...(!next && { cache: 'no-store' }),
        headers,
        next,
      },
    );
  } catch (error) {
    logError('Fetch request failed', {
      error: error instanceof Error ? error.message : error,
      functionName: 'getListings',
      method: 'GET',
      statusCode: 'NETWORK_ERROR',
      url,
      ...(error instanceof Error && 'cause' in error
        ? {
            cause: error.cause,
            causeCode: (error.cause as { code?: string } | undefined)?.code,
          }
        : {}),
    });

    throw error;
  }

  // TODO: make error handle!
  if (!listingsResponse.ok) {
    sendHttpError({
      baseName: 'Get listings for search',
      functionName: 'getListings',
      method: 'GET',
      requestHeaders: headers,
      statusCode: listingsResponse.status,
      url,
    });

    logWarn('Listings search request failed', {
      responseBody: await listingsResponse.json(),
      statusCode: listingsResponse.status,
      url,
    });

    throw new Error('Failed to fetch data');
  }

  const listingsResponseData = await listingsResponse.json() as ListingsSearchResponse;

  let listings: ListingWithUser[] = [];

  if (listingsResponseData.items.length > 0) {
    const listingsUsersIds = listingsResponseData.items.map((listingItem) => listingItem.user.id);
    let usersVerifications: UsersIdVerificationsCollection = {};

    if (includeUserVerifications) {
      try {
        usersVerifications = await getUsersIdVerifications({ usersIds: listingsUsersIds, next });
      } catch (error) {
        logWarn('Continuing listings response without ID verifications', {
          error: error instanceof Error ? error.message : error,
          listingsUsersCount: listingsUsersIds.length,
          url,
        });
      }
    }

    listings = listingsResponseData.items.map((listingItem) => {
      const listingWithUser: ListingWithUser = {
        ...listingAdapter(listingItem.listing),
        user: userAdapter(listingItem.user),
      };

      const userVerification = usersVerifications[listingItem.user.id];

      if (userVerification) {
        listingWithUser.user.verifications.id = userVerification;
      }

      return listingWithUser;
    });
  }

  return {
    averageMonthRent: listingsResponseData.avg_monthly_rent,
    count: listingsResponseData.count,
    items: listings,
    searchGeoBbox: getSearchGeoBbox(listingsResponseData.geo),
  };
});
